"use client";

import { DEMO_CALENDAR_LINK } from "@/lib/constants/booking";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import Image from "next/image";
import Link from "next/link";

export default function OnePager() {
  // Hooks
  const { openModal, closeModal, isModalOpen } = useModalManager();

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-16 px-4">
        <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-12 py-16 text-white">
            <Link href="/" className="inline-block mb-6 hover:opacity-80 transition-opacity">
              <Image src="/nowadays_white.svg" alt="Nowadays AI" width={300} height={80} priority />
            </Link>
            <h2 className="text-3xl font-semibold mb-4">Your AI Assistant for Corporate Offsites</h2>
            <p className="text-xl text-blue-100 max-w-3xl">
              Purpose-built for executive assistants who plan multiple team offsites, retreats, and corporate events.
              Streamline your workflow and save hours on venue research and coordination.
            </p>
          </div>

          {/* Main Content */}
          <div className="px-12 py-12 space-y-12">
            {/* The Problem Section */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="text-2xl">⚡</span>
                The Challenge
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg">
                  <p className="text-gray-800">
                    <span className="font-semibold text-red-700">Endless venue research:</span> Hours spent comparing
                    options across multiple websites and spreadsheets
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg">
                  <p className="text-gray-800">
                    <span className="font-semibold text-red-700">Email overload:</span> Drowning in venue quote emails
                    and losing track of responses
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg">
                  <p className="text-gray-800">
                    <span className="font-semibold text-red-700">Manual follow-ups:</span> Chasing venues for quotes and
                    availability updates
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg">
                  <p className="text-gray-800">
                    <span className="font-semibold text-red-700">Stakeholder coordination:</span> Multiple rounds of
                    approvals and preference changes
                  </p>
                </div>
              </div>
            </section>

            {/* The Solution Section */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="text-2xl">✨</span>
                How Nowadays AI Helps
              </h3>
              <div className="space-y-6">
                <div className="flex gap-6 items-start bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                  <div className="text-3xl flex-shrink-0">🔍</div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">AI-Powered Venue Discovery</h4>
                    <p className="text-gray-700">
                      Input your event details once, and our AI instantly surfaces the best venue options from our
                      network of 400K+ partners worldwide. Filter by location, capacity, budget, and amenities.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6 items-start bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                  <div className="text-3xl flex-shrink-0">📧</div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Automated Email Management</h4>
                    <p className="text-gray-700">
                      Connect your Gmail and let our AI automatically parse venue quote emails, extract key details, and
                      organize everything in one dashboard. No more digging through your inbox.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6 items-start bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                  <div className="text-3xl flex-shrink-0">💰</div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">AI-Negotiated Rates</h4>
                    <p className="text-gray-700">
                      Our AI handles outreach and negotiation, often securing rates 25-30% lower than original quotes.
                      IATA accreditation ensures you get the best agency rates and discounts.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6 items-start bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                  <div className="text-3xl flex-shrink-0">👥</div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Seamless Team Collaboration</h4>
                    <p className="text-gray-700">
                      Share event details with stakeholders, collect feedback, and get approvals—all within the
                      platform. Keep everyone aligned without endless email threads.
                    </p>
                  </div>
                </div>

                <div className="flex gap-6 items-start bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                  <div className="text-3xl flex-shrink-0">⏱️</div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">Time-Saving Automation</h4>
                    <p className="text-gray-700">
                      What typically takes 15-20 hours of research now takes minutes. Focus on the strategic work while
                      AI handles the tedious tasks.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Perfect For Section */}
            <section className="bg-gradient-to-r from-purple-50 to-pink-50 p-8 rounded-xl border border-purple-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="text-2xl">👔</span>
                Perfect For Executive Assistants Who...
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-800">
                <div className="flex items-start gap-3">
                  <span className="text-green-600 text-xl flex-shrink-0">✓</span>
                  <p>Plan 5+ corporate offsites or retreats per year</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-green-600 text-xl flex-shrink-0">✓</span>
                  <p>Manage events for teams of 20-2000+ people</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-green-600 text-xl flex-shrink-0">✓</span>
                  <p>Coordinate across multiple departments</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-green-600 text-xl flex-shrink-0">✓</span>
                  <p>Need to demonstrate ROI and cost savings</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-green-600 text-xl flex-shrink-0">✓</span>
                  <p>Want to standardize offsite planning process</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-green-600 text-xl flex-shrink-0">✓</span>
                  <p>Support C-suite and senior leadership</p>
                </div>
              </div>
            </section>

            {/* ROI Section */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <span className="text-2xl">📊</span>
                The ROI for Your Organization
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-xl border border-green-200">
                  <div className="text-5xl font-bold text-green-600 mb-2">70%</div>
                  <p className="text-gray-800 font-medium">Time Saved</p>
                  <p className="text-sm text-gray-600 mt-2">Reduce planning time from weeks to days</p>
                </div>
                <div className="text-center bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-xl border border-blue-200">
                  <div className="text-5xl font-bold text-blue-600 mb-2">30%</div>
                  <p className="text-gray-800 font-medium">Cost Savings</p>
                  <p className="text-sm text-gray-600 mt-2">Lower venue rates through AI negotiation</p>
                </div>
                <div className="text-center bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-xl border border-purple-200">
                  <div className="text-5xl font-bold text-purple-600 mb-2">400K+</div>
                  <p className="text-gray-800 font-medium">Venue Network</p>
                  <p className="text-sm text-gray-600 mt-2">Access to global venue partnerships</p>
                </div>
              </div>
            </section>

            {/* Platform Features Section */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Complete Planning Platform</h3>
              <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
                Everything you need to plan, manage, and execute successful corporate events—all in one place.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Link
                  href="/venue-sourcing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-4 bg-white border border-gray-200 rounded-lg hover:border-indigo-400 hover:shadow-md transition-all duration-200 group"
                >
                  <div className="text-2xl mb-2">🔍</div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1 group-hover:text-indigo-600 transition-colors">
                    Venue Sourcing
                  </h4>
                  <p className="text-xs text-gray-600">Find perfect venues</p>
                </Link>

                <Link
                  href="/flight-estimator"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-4 bg-white border border-gray-200 rounded-lg hover:border-indigo-400 hover:shadow-md transition-all duration-200 group"
                >
                  <div className="text-2xl mb-2">✈️</div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1 group-hover:text-indigo-600 transition-colors">
                    Flight Estimator
                  </h4>
                  <p className="text-xs text-gray-600">Estimate travel costs</p>
                </Link>

                <Link
                  href="/budget-estimator"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-4 bg-white border border-gray-200 rounded-lg hover:border-indigo-400 hover:shadow-md transition-all duration-200 group"
                >
                  <div className="text-2xl mb-2">💰</div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1 group-hover:text-indigo-600 transition-colors">
                    Budget Estimator
                  </h4>
                  <p className="text-xs text-gray-600">Track event budgets</p>
                </Link>

                <Link
                  href="/registration"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-4 bg-white border border-gray-200 rounded-lg hover:border-indigo-400 hover:shadow-md transition-all duration-200 group"
                >
                  <div className="text-2xl mb-2">👥</div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1 group-hover:text-indigo-600 transition-colors">
                    Registration
                  </h4>
                  <p className="text-xs text-gray-600">Attendee management</p>
                </Link>

                <Link
                  href="/create"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-4 bg-white border border-gray-200 rounded-lg hover:border-indigo-400 hover:shadow-md transition-all duration-200 group"
                >
                  <div className="text-2xl mb-2">📝</div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1 group-hover:text-indigo-600 transition-colors">
                    Create Event
                  </h4>
                  <p className="text-xs text-gray-600">Create RFPs for venues</p>
                </Link>

                <Link
                  href="/analytics"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-4 bg-white border border-gray-200 rounded-lg hover:border-indigo-400 hover:shadow-md transition-all duration-200 group"
                >
                  <div className="text-2xl mb-2">📊</div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1 group-hover:text-indigo-600 transition-colors">
                    Analytics
                  </h4>
                  <p className="text-xs text-gray-600">Track event metrics</p>
                </Link>

                <Link
                  href="/activations"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-4 bg-white border border-gray-200 rounded-lg hover:border-indigo-400 hover:shadow-md transition-all duration-200 group"
                >
                  <div className="text-2xl mb-2">🎯</div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1 group-hover:text-indigo-600 transition-colors">
                    Activations
                  </h4>
                  <p className="text-xs text-gray-600">Engaging your clients</p>
                </Link>
              </div>
            </section>

            {/* Testimonial */}
            <section className="bg-gradient-to-r from-indigo-50 to-blue-50 p-8 rounded-xl border-l-4 border-indigo-500">
              <div className="flex items-start gap-6">
                <div className="text-6xl text-indigo-400">"</div>
                <div>
                  <p className="text-lg text-gray-800 italic mb-4">
                    Using Nowadays, I secured bids from hotel venues with beautiful facilities at prices I'd never been
                    able to negotiate before. Nowadays found us the perfect venue that fit all of our criteria.
                  </p>
                  <p className="text-gray-900 font-semibold">— Yeon Baik, Senior Manager at Google</p>
                </div>
              </div>
            </section>

            {/* Trusted By Section */}
            <section>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Trusted By Teams At</h3>
              <div className="flex flex-wrap justify-center items-center gap-12">
                <div className="opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-300">
                  <Image src="/landing/google.svg" alt="Google" width={100} height={35} />
                </div>
                <div className="opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-300">
                  <Image src="/landing/notion.svg" alt="Notion" width={80} height={28} />
                </div>
                <div className="opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-300">
                  <Image src="/landing/montecarlo.png" alt="Monte Carlo Data" width={120} height={40} />
                </div>
                <div className="opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-300">
                  <Image src="/landing/gofundme.svg" alt="GoFundMe" width={120} height={40} />
                </div>
                <div className="opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-300">
                  <Image src="/landing/blackrock.svg" alt="BlackRock" width={120} height={40} />
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="bg-gradient-to-r from-indigo-600 to-blue-600 p-12 rounded-xl text-white text-center">
              <h3 className="text-3xl font-bold mb-4">Ready to Transform Your Offsite Planning?</h3>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Schedule a 15-minute demo to see how Nowadays AI can streamline your workflow and save your team time
                and money.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => openModal("demo")}
                  className="bg-white text-indigo-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-colors"
                >
                  Schedule a Demo
                </button>
                <Link
                  href="/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-indigo-800 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-indigo-900 transition-colors border border-white/20 inline-block"
                >
                  Learn More
                </Link>
              </div>
            </section>

            {/* Contact Info */}
            <section className="text-center border-t border-gray-200 pt-8">
              <p className="text-gray-600 mb-2">Questions? We're here to help.</p>
              <div className="flex justify-center gap-6 text-sm text-gray-600">
                <a href="mailto:founders@getnowadays.com" className="hover:text-indigo-600 transition-colors">
                  founders@getnowadays.com
                </a>
                <span>•</span>
                <a
                  href="https://www.nowadays.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-indigo-600 transition-colors"
                >
                  www.nowadays.ai
                </a>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Demo Modal */}
      <UnifiedModal
        type="cal"
        isOpen={isModalOpen("demo")}
        onClose={() => closeModal("demo")}
        title="Book a Demo"
        subtitle="Schedule a 15-minute call to see how Nowadays AI can help your team"
        calLink={DEMO_CALENDAR_LINK}
      />
    </>
  );
}
